import type { FeedbackStatus, PageMetadata } from '../types';
import { identifyComponent, getElementInfo } from './componentLocator';
import { getMarkupWindow, useDeviceFrameStore } from './deviceFrame';
import { useMarkupStore } from '../store/useMarkupStore';

/** A region of the tab, in CSS pixels, to cut out of a captured frame. */
interface CropRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Grab a single still frame from a display-capture MediaStream and return it as
 * a PNG data URL. Mirrors how the screen recording feature consumes the same
 * getDisplayMedia stream, but takes one frame instead of recording.
 */
/** Play a display-capture stream into a detached video element. */
async function playStream(stream: MediaStream): Promise<HTMLVideoElement> {
  const video = document.createElement('video');
  video.srcObject = stream;
  video.muted = true;
  (video as HTMLVideoElement).playsInline = true;

  // Wait until the video has real dimensions before drawing.
  await new Promise<void>((resolve, reject) => {
    const onReady = () => {
      if (video.videoWidth && video.videoHeight) resolve();
    };
    video.onloadedmetadata = onReady;
    video.onloadeddata = onReady;
    video.onerror = () => reject(new Error('Failed to load capture stream'));
    video.play().then(onReady).catch(reject);
  });
  return video;
}

/**
 * Resolve once the capture has caught up with the page: a couple of new
 * frames have arrived, or none has for a while (tab capture only sends frames
 * when something changes, so silence means the last one is current).
 *
 * The capture runs behind the page. Grabbing straight after hiding the widget
 * drew a frame from *before* it was hidden, so the panel and the responsive
 * toolbar ended up in the screenshot anyway.
 */
function waitForFreshFrame(video: HTMLVideoElement): Promise<void> {
  return new Promise((resolve) => {
    let frames = 0;
    let quiet = window.setTimeout(resolve, 250);
    const rvfc = (video as HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: () => void) => number;
    }).requestVideoFrameCallback?.bind(video);
    if (!rvfc) return;
    const onFrame = () => {
      window.clearTimeout(quiet);
      if (++frames >= 2) return resolve();
      quiet = window.setTimeout(resolve, 250);
      rvfc(onFrame);
    };
    rvfc(onFrame);
  });
}

function grabFrame(video: HTMLVideoElement, crop?: CropRect): string {
  // Captured video is in device pixels of whatever was shared. Only crop when
  // it's plainly this tab — same aspect ratio as the window — otherwise the
  // reviewer picked a different screen or window and the rectangle means
  // nothing in it.
  const ratio = video.videoWidth / window.innerWidth;
  const isThisTab =
    Math.abs(video.videoHeight / window.innerHeight - ratio) < 0.05 * ratio;
  const src =
    crop && isThisTab
      ? {
          x: Math.max(0, Math.round(crop.left * ratio)),
          y: Math.max(0, Math.round(crop.top * ratio)),
          w: Math.round(crop.width * ratio),
          h: Math.round(crop.height * ratio),
        }
      : { x: 0, y: 0, w: video.videoWidth, h: video.videoHeight };

  const canvas = document.createElement('canvas');
  canvas.width = src.w;
  canvas.height = src.h;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D canvas context');
  ctx.drawImage(video, src.x, src.y, src.w, src.h, 0, 0, src.w, src.h);

  return canvas.toDataURL('image/png');
}

/** How long the panel takes to slide off — its `duration-300`, plus a beat. */
const STEP_ASIDE_MS = 350;

/**
 * Capture a screenshot of the current tab using the native screen-capture API
 * (the same getDisplayMedia flow the screen recorder uses), rather than
 * re-rendering the DOM with html2canvas. This produces a pixel-accurate shot.
 *
 * Once the tab is being shared, the panel slides off to the right and the
 * responsive toolbar hides, then pins and anything left of the widget are
 * hidden outright, and the frame is only grabbed once the capture shows all
 * of that. With a device preview
 * open, the shot is cropped to the device, so it comes out phone-shaped rather
 * than as a phone floating on a dark backdrop.
 */
export async function captureScreenshot(_clickElement?: Element): Promise<string> {
  // Found through its window rather than by selector: the standalone build
  // renders inside a Shadow DOM, where document.querySelector can't see it.
  const deviceFrame = (useDeviceFrameStore.getState().frame?.frameElement ??
    null) as HTMLIFrameElement | null;

  // The preview draws its own pins layer, so there can be more than one — and
  // it may sit in a shadow root alongside the frame.
  const roots = new Set<Document | ShadowRoot>([document]);
  const frameRoot = deviceFrame?.getRootNode();
  if (frameRoot instanceof ShadowRoot) roots.add(frameRoot);
  const hidden = Array.from(roots).flatMap((root) =>
    Array.from(
      root.querySelectorAll<HTMLElement>(
        '[data-markup-widget], [data-markup-pins], [data-markup-viewport]',
      ),
    ),
  );

  const { setIsSteppingAside } = useMarkupStore.getState();
  let stream: MediaStream | null = null;
  let video: HTMLVideoElement | null = null;

  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: 30 },
      audio: false,
      // Prefer capturing the current tab so the user isn't asked to pick a
      // source (supported on Chromium; ignored elsewhere).
      preferCurrentTab: true,
    } as MediaStreamConstraints & { preferCurrentTab?: boolean });

    // Live before anything moves, so the frames that follow are watched.
    video = await playStream(stream);

    // Slide the panel away (and drop the responsive toolbar), let it finish,
    // then hide whatever's left instantly — pins, and the widget too if the
    // host's CSS kept it from moving.
    setIsSteppingAside(true);
    await new Promise((resolve) => setTimeout(resolve, STEP_ASIDE_MS));
    hidden.forEach((el) => (el.style.visibility = 'hidden'));
    await waitForFreshFrame(video);

    const crop = deviceFrame?.getBoundingClientRect();
    return grabFrame(video, crop && crop.width > 0 ? crop : undefined);
  } catch (error) {
    console.error('Failed to capture screenshot:', error);
    throw error;
  } finally {
    // Always bring the UI back and stop the capture stream.
    hidden.forEach((el) => (el.style.visibility = ''));
    setIsSteppingAside(false);
    if (video) {
      video.pause();
      video.srcObject = null;
    }
    stream?.getTracks().forEach((track) => track.stop());
  }
}

const detectDeviceType = (
  view: Window = window,
): 'desktop' | 'tablet' | 'mobile' => {
  const width = view.innerWidth;
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check user agent for mobile/tablet indicators
  const isMobileUA = /mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  const isTabletUA = /tablet|ipad|playbook|silk|kindle/i.test(userAgent);
  
  // Prioritize user agent, then fall back to screen width
  if (isMobileUA && width < 768) return 'mobile';
  if (isTabletUA || (width >= 768 && width < 1024)) return 'tablet';
  return 'desktop';
};

/**
 * Describe the page being marked up. Defaults to the device preview while one
 * is open, so the URL and dimensions are the ones the reviewer was looking at
 * — they may have navigated inside the preview, and its viewport is the
 * device's, not the desktop window's.
 */
export function getPageMetadata(
  clickElement?: Element,
  view: Window = getMarkupWindow(),
): PageMetadata {
  return {
    url: view.location.href,
    title: view.document.title,
    screenWidth: view.innerWidth,
    screenHeight: view.innerHeight,
    devicePixelRatio: view.devicePixelRatio,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    deviceType: detectDeviceType(view),
    componentName: clickElement ? identifyComponent(clickElement) : undefined,
    elementInfo: clickElement ? getElementInfo(clickElement) : undefined,
  };
}

export function getElementSelector(element: Element): string {
  if (element.id) {
    return `#${element.id}`;
  }

  const path: string[] = [];
  let current: Element | null = element;
  const body = element.ownerDocument.body;

  while (current && current !== body) {
    let selector = current.tagName.toLowerCase();

    if (current.className && typeof current.className === 'string') {
      const classes = current.className
        .split(' ')
        .filter((c) => c.trim())
        .slice(0, 2)
        .join('.');
      if (classes) {
        selector += `.${classes}`;
      }
    }

    const siblings = current.parentElement?.children;
    if (siblings && siblings.length > 1) {
      const index = Array.from(siblings).indexOf(current);
      selector += `:nth-child(${index + 1})`;
    }

    path.unshift(selector);
    current = current.parentElement;
  }

  return path.join(' > ');
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/**
 * Reduce a URL to the identity of the *page* it points at, for deciding which
 * pins belong on screen.
 *
 * `exact` compares the raw href — two visits only match if the URL is
 * character-identical. `pathname` (the default) ignores the query string, the
 * hash, and a trailing slash, so a pin placed on `/about` still shows up when
 * the visitor arrives via `/about/?utm_source=newsletter#team`. Ad campaign
 * params, tracking params and anchor links are the common reason pins look
 * like they've vanished.
 *
 * `pathname` ignores the origin too. Tickets are already scoped to one site by
 * the project, so a different origin is just a different copy of it — staging,
 * a preview deploy, localhost — and a pin raised on staging's `/about` belongs
 * on `localhost:3000/about` while the site is being fixed.
 *
 * Falls back to the trimmed raw string if the URL can't be parsed.
 */
export function normalizePageUrl(
  url: string | undefined | null,
  mode: 'pathname' | 'exact' = 'pathname'
): string {
  if (!url) return '';
  if (mode === 'exact') return url;

  try {
    const parsed = new URL(url);
    // Drop a single trailing slash so /about and /about/ are one page, but
    // keep the root as "/" rather than an empty string.
    return parsed.pathname.replace(/\/+$/, '') || '/';
  } catch (_) {
    return url.trim();
  }
}

/** True when two URLs refer to the same page under the given match mode. */
export function isSamePage(
  a: string | undefined | null,
  b: string | undefined | null,
  mode: 'pathname' | 'exact' = 'pathname'
): boolean {
  if (!a || !b) return false;
  return normalizePageUrl(a, mode) === normalizePageUrl(b, mode);
}

/**
 * Whether a ticket is in scope for the current status filter.
 *
 * `all` means all *active*: closed tickets are finished work, so they stay out
 * of both the list and the on-page pins until someone asks for them by name.
 * Shared by the list and the pin overlay so the two always agree about what's
 * on screen.
 */
export function matchesStatusFilter(
  status: FeedbackStatus,
  filter: FeedbackStatus | 'all'
): boolean {
  if (filter === 'all') return status !== 'closed';
  return status === filter;
}

/**
 * Parse a `archiveBefore` config value into a cutoff Date.
 *
 * Accepts a Date, an ISO date (`2026-07-24`, or a full ISO timestamp), or a
 * day-first `DD/MM/YYYY` / `DD-MM-YYYY` string. Day-first is deliberate:
 * `24/07/2026` means 24 July, and `07/06/2026` means 6 June, not 7 June.
 * Use the ISO form if you'd rather not think about it.
 *
 * Bare dates resolve to local midnight, so "archive before 24/07/2026" means
 * the start of the 24th where the user is, not UTC. Returns null for empty or
 * unparseable values, which callers treat as "no cutoff".
 */
export function parseArchiveBefore(
  value: string | Date | undefined | null
): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  // Bare ISO date — build it locally rather than letting Date parse it as UTC.
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const [, y, m, d] = iso;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  // Day-first DD/MM/YYYY or DD-MM-YYYY.
  const dayFirst = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dayFirst) {
    const [, d, m, y] = dayFirst;
    const day = Number(d);
    const month = Number(m);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const parsed = new Date(Number(y), month - 1, day);
    // Reject overflow like 31/02/2026, which Date would roll into March.
    if (parsed.getMonth() !== month - 1 || parsed.getDate() !== day) return null;
    return parsed;
  }

  // Anything else (full ISO timestamps, RFC strings) goes to the engine.
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    console.warn(
      `Markup: could not parse archiveBefore value "${trimmed}". ` +
        `Use YYYY-MM-DD or DD/MM/YYYY. Ignoring it — no tickets will be archived.`
    );
    return null;
  }
  return parsed;
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'open':
      return '#3b82f6';
    case 'in-progress':
      return '#f59e0b';
    case 'resolved':
      return '#10b981';
    case 'closed':
      return '#6b7280';
    default:
      return '#6b7280';
  }
}

export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'critical':
      return '#ef4444';
    case 'high':
      return '#f97316';
    case 'medium':
      return '#eab308';
    case 'low':
      return '#22c55e';
    default:
      return '#6b7280';
  }
}

export function downloadScreenshot(dataUrl: string, filename: string = 'screenshot.png'): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

/**
 * Force a cursor across the page while a pick mode is active. Setting
 * `body.style.cursor` isn't enough: sites with a custom cursor hide the native
 * one on every element (`* { cursor: none }`), so nothing inherits it. The
 * widget's own cursor rules are more specific, so it keeps its cursors.
 * Returns a cleanup that removes the override.
 */
export function forcePageCursor(doc: Document, cursor: string): () => void {
  const style = doc.createElement('style');
  style.setAttribute('data-markup-cursor', '');
  style.textContent = `html body, html body * { cursor: ${cursor} !important; }`;
  doc.head.appendChild(style);
  return () => style.remove();
}
